<template>
  <div id="insta-home">
    <!-- Hero Section -->
    <div class="hero-section">
      <h1 class="hero-title">Instagram 맞팔 확인</h1>
      <p class="hero-subtitle">
        누가 나를 언팔했을까? 데이터를 업로드하고 바로 확인하세요.
      </p>
      <router-link to="/insta/unfollower" class="hero-cta">지금 확인하기</router-link>
    </div>

    <!-- Feature Cards -->
    <div class="feature-cards">
      <div class="feature-card">
        <div class="feature-icon">🔍</div>
        <div class="feature-title">언팔 감지</div>
        <div class="feature-desc">
          내가 팔로우하지만 나를 팔로우하지 않는 사람을 찾아줍니다.
        </div>
      </div>
      <div class="feature-card">
        <div class="feature-icon">📁</div>
        <div class="feature-title">ZIP 업로드</div>
        <div class="feature-desc">
          인스타그램에서 받은 ZIP 파일을 그대로 업로드하면 자동 분석합니다.
        </div>
      </div>
      <div class="feature-card">
        <div class="feature-icon">🔒</div>
        <div class="feature-title">완전한 개인정보 보호</div>
        <div class="feature-desc">
          어떠한 정보도 서버로 전송되지 않습니다. 오프라인에서도 작동합니다.
        </div>
      </div>
    </div>

    <!-- Guide Section -->
    <div class="guide-section">
      <h2 class="section-title">이용 방법</h2>
      <p class="section-subtitle">
        인스타그램 데이터를 다운로드하는 방법을 안내합니다.
      </p>

      <!-- Tab -->
      <div class="guide-tab-area">
        <button
          class="guide-tab"
          :class="{ active: device === 'pc' }"
          @click="device = 'pc'"
        >
          PC
        </button>
        <button
          class="guide-tab"
          :class="{ active: device === 'mobile' }"
          @click="device = 'mobile'"
        >
          Mobile
        </button>
      </div>

      <!-- PC Guide -->
      <div class="guide-steps" v-if="device === 'pc'">
        <div class="guide-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <div class="step-title">데이터 다운로드 페이지 이동</div>
            <div class="step-desc">
              아래 링크를 클릭하여 인스타그램 데이터 다운로드 페이지로
              이동하세요. 이동 후
              <span class="highlight">계속</span> 버튼을 클릭합니다.
            </div>
            <div class="step-link">
              <a
                href="https://www.instagram.com/download/request/"
                target="_blank"
                rel="noopener noreferrer"
                >인스타그램 데이터 다운로드 →</a
              >
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc1.png')"
                alt="데이터 다운로드 페이지"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <div class="step-title">정보 다운로드 또는 전송 선택</div>
            <div class="step-desc">
              내 정보 다운로드 항목에서
              <span class="highlight">정보 다운로드 또는 전송</span>을
              클릭하세요.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc2.png')"
                alt="정보 다운로드 또는 전송"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <div class="step-title">계정 선택 및 일부 정보 선택</div>
            <div class="step-desc">
              내 계정 및 프로필에서 원하는 계정을 선택한 뒤,
              <span class="highlight">일부 정보 항목</span>을 선택하세요.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc3.png')"
                alt="계정 선택"
              />
              <img
                :src="require('@/insta/assets/img/guide/pc4.png')"
                alt="일부 정보 항목"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">4</div>
          <div class="step-content">
            <div class="step-title">팔로워 및 팔로잉 체크</div>
            <div class="step-desc">
              연결 활동 영역에서
              <span class="highlight-red">팔로워 및 팔로잉</span> 항목만
              체크하세요.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc5.png')"
                alt="팔로워 및 팔로잉 체크"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">5</div>
          <div class="step-content">
            <div class="step-title">다운로드 위치 선택</div>
            <div class="step-desc">
              데이터를 받을 위치를 선택합니다.
            </div>
            <ul class="step-list">
              <li>
                <span class="highlight">기기에 다운로드</span> — 모바일
                인스타그램 앱에서 다운로드
              </li>
              <li>
                <span class="highlight">전송 대상</span> — 구글 드라이브 또는
                드롭박스로 전송 (PC 추천)
              </li>
            </ul>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc6.png')"
                alt="다운로드 위치 선택"
              />
              <img
                :src="require('@/insta/assets/img/guide/pc7.png')"
                alt="전송 대상 선택"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">6</div>
          <div class="step-content">
            <div class="step-title">구글 드라이브 연결</div>
            <div class="step-desc">
              구글 드라이브를 선택하면 계정 로그인 및 권한 확인이 진행됩니다.
              빈도 설정을 통해 주기적으로 데이터를 받을 수도 있습니다.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc8.png')"
                alt="구글 드라이브 선택"
              />
              <img
                :src="require('@/insta/assets/img/guide/pc9.png')"
                alt="구글 계정 로그인"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">7</div>
          <div class="step-content">
            <div class="step-title">JSON 형식 선택 및 파일 만들기</div>
            <div class="step-desc">
              데이터 형식을 반드시
              <span class="highlight-red">JSON</span>으로 변경해 주세요.
              미디어 품질은 상관없습니다. 확인 후
              <span class="highlight">파일 만들기</span>를 클릭하세요.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc11.png')"
                alt="JSON 형식 선택"
              />
              <img
                :src="require('@/insta/assets/img/guide/pc12.png')"
                alt="파일 만들기"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">8</div>
          <div class="step-content">
            <div class="step-title">파일 생성 대기 및 다운로드</div>
            <div class="step-desc">
              진행 중 목록에 전송 상태가 표시됩니다. 완료까지 다소 시간이
              소요됩니다. 완료되면 인스타그램 또는 구글 드라이브에서 파일을
              다운로드하세요.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc13.png')"
                alt="전송 진행 중"
              />
              <img
                :src="require('@/insta/assets/img/guide/pc14.png')"
                alt="다운로드 가능"
              />
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">9</div>
          <div class="step-content">
            <div class="step-title">ZIP 파일 업로드</div>
            <div class="step-desc">
              다운로드 받은
              <span class="highlight-red">ZIP 파일을 그대로 업로드</span
              >하면 자동으로 분석됩니다. 압축을 풀 필요가 없습니다.
            </div>
            <div class="step-images">
              <img
                :src="require('@/insta/assets/img/guide/pc15.png')"
                alt="다운로드된 ZIP 파일"
              />
            </div>
            <div class="step-link">
              <router-link to="/insta/unfollower">언팔로워 찾으러 가기 →</router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile Guide -->
      <div class="guide-steps" v-else-if="device === 'mobile'">
        <div class="guide-step">
          <div class="step-number">1</div>
          <div class="step-content">
            <div class="step-title">인스타그램 설정 이동</div>
            <div class="step-desc">
              인스타그램 앱에서 프로필 화면 오른쪽 상단의
              <span class="highlight">≡ (메뉴)</span>를 탭하세요. 그 다음
              <span class="highlight">설정 및 개인정보</span>를 선택합니다.
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">2</div>
          <div class="step-content">
            <div class="step-title">계정 센터에서 정보 다운로드</div>
            <div class="step-desc">
              아래 경로를 순서대로 따라가세요.
            </div>
            <div class="step-path">
              <span class="path-item">설정 및 개인정보</span>
              <span class="path-arrow">›</span>
              <span class="path-item">계정 센터</span>
              <span class="path-arrow">›</span>
              <span class="path-item">내 정보 및 권한</span>
              <span class="path-arrow">›</span>
              <span class="path-item">내 정보 다운로드</span>
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">3</div>
          <div class="step-content">
            <div class="step-title">팔로워 및 팔로잉 데이터 요청</div>
            <div class="step-desc">
              <span class="highlight">일부 정보</span>를 선택한 후
              <span class="highlight">팔로워 및 팔로잉</span> 항목만
              체크하세요.
            </div>
            <div class="step-warning">
              형식은 반드시 <span class="highlight-red">JSON</span>으로
              선택해 주세요!
            </div>
            <div class="step-desc">
              설정 후 <span class="highlight">파일 만들기</span>를 진행하세요.
            </div>
          </div>
        </div>

        <div class="guide-step">
          <div class="step-number">4</div>
          <div class="step-content">
            <div class="step-title">파일 다운로드 후 업로드</div>
            <div class="step-desc">
              인스타그램에서 파일 준비가 완료되면 알림이 옵니다. 파일을
              다운로드한 뒤
              <span class="highlight-red">ZIP 파일을 그대로 업로드</span
              >하거나, 압축 해제 후
              <span class="highlight">followers_1.json</span>과
              <span class="highlight">following.json</span>을 업로드하세요.
            </div>
            <div class="step-link">
              <router-link to="/insta/unfollower">언팔로워 찾으러 가기 →</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "InstaHomeView",

  data() {
    return {
      device: "pc" as "pc" | "mobile",
    };
  },
});
</script>
